import type { ButtonProps as MuiButtonProps } from "@mui/material";
import clsx from "clsx";
import { memo, useMemo, type MouseEvent, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Link } from "./Link";

export interface ButtonProps {
    isLoading?: boolean;
    variant?: "primary" | "secondary" | "ghost" | "text" | "filter-active" | "filter-inactive";
    color?: MuiButtonProps["color"];
    fullWidth?: boolean;
    type?: "submit" | "button";
    disabled?: boolean;
    children: ReactNode;
    textGlow?: boolean;
    loadingText?: string;
    size?: "sm" | "md" | "lg" | "xs" | "icon";
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
    className?: string;
    textClassName?: string;
    href?: string;
    ref?: React.Ref<HTMLButtonElement> | React.Ref<HTMLAnchorElement>;
    endIcon?: ReactNode;
}

export const Button = memo(function Button({
    children,
    isLoading,
    loadingText,
    variant = "primary",
    size = "md",
    fullWidth,
    type = "button",
    disabled,
    href,
    ref,
    endIcon,
    onClick,
    className,
    textClassName,
    ...props
}: ButtonProps) {
    const isDisabled = disabled || isLoading;

    const dynamicClass = useMemo(
        () =>
            twMerge(
                clsx(
                    "btn select-none",
                    {
                        "btn-primary": variant === "primary",
                        "btn-secondary": variant === "secondary",
                        "btn-ghost": variant === "ghost",
                        "btn-text": variant === "text",
                        "btn-filter-active": variant === "filter-active",
                        "btn-filter-inactive": variant === "filter-inactive",
                        "btn-size-xs": size === "xs",
                        "btn-size-sm": size === "sm",
                        "btn-size-md": size === "md",
                        "btn-size-lg": size === "lg",
                        "btn-size-icon": size === "icon",
                        "btn-width-full": fullWidth,
                        "btn-width-fit": !fullWidth,
                        "btn-disabled": isDisabled,
                        "btn-no-hover": isDisabled,
                    },
                    className,
                ),
            ),
        [className, fullWidth, isDisabled, size, variant],
    );

    const content = useMemo(() => {
        const renderedChildren = isLoading ? (loadingText ?? "Loading...") : children;

        if (!textClassName) {
            return renderedChildren;
        }

        return <span className={twMerge("min-w-0", textClassName)}>{renderedChildren}</span>;
    }, [children, isLoading, loadingText, textClassName]);

    if (href) {
        const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
            if (isDisabled) {
                event.preventDefault();
                return;
            }
            onClick?.(event);
        };

        return (
            <Link
                ref={ref as React.Ref<HTMLAnchorElement>}
                href={href}
                aria-disabled={isDisabled}
                onClick={handleLinkClick}
                {...props}
                className={dynamicClass}
            >
                {content}
                {endIcon && <span className="ml-2">{endIcon}</span>}
            </Link>
        );
    }

    return (
        <button
            ref={ref as React.Ref<HTMLButtonElement>}
            type={type}
            disabled={isDisabled}
            onClick={onClick as ((event: MouseEvent<HTMLButtonElement>) => void) | undefined}
            {...props}
            className={dynamicClass}
        >
            {content}
            {endIcon && <span className="ml-2">{endIcon}</span>}
        </button>
    );
});

Button.displayName = "Button";
